import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPriceExceptionData } from "src/api/sp";
import { resetStore } from "src/store/actions";
import {
  formatToCurrency,
  getComponentKey,
  getItemTypes
} from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import {
  PriceExceptionInitialState,
  PriceExceptionRequestParams
} from "../../../config/tableInterface/interface";
import { getFormatedDate, getFormattedDate } from "src/utils/getDates";
import {
  talentDateFormat,
  vendorTypeMapper,
  ITEM_TAGS_MAPPER
} from "src/config/constants/genericConstants";

export const fetchPriceExceptionData = createAsyncThunk(
  "viewItems/fetchPriceExceptionData",
  (payload: PriceExceptionRequestParams) => getPriceExceptionData(payload)
);

const initialState: PriceExceptionInitialState = {
  data: null,
  recordsCount: null,
  status: "idle",
  error: null
};

const viewPriceExceptionData = createSlice({
  name: "viewPriceExceptionData",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchPriceExceptionData.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchPriceExceptionData.fulfilled, (state, { payload }) => {
        state.status = "idle";
        if (payload?.data?.length > 0) {
          state.data = payload?.data?.map((item: any) => {
            const itemType = vendorTypeMapper[item?.itemDSDDC];
            const goiRetailValue = stringToNumber(item.goiRetail);
            const storeRetailValue = stringToNumber(item.storeRetail);
            const goiRetail = formatToCurrency(goiRetailValue);
            const storeRetail = formatToCurrency(storeRetailValue);
            const goiPriceChangeDate = getFormatedDate(
              item?.goiPriceChangeDate,
              talentDateFormat
            );
            const storePriceChangeDate = getFormatedDate(
              item?.storePriceChangeDate,
              talentDateFormat
            );
            const lastSoldDate = getFormatedDate(
              item?.lastSoldDate,
              talentDateFormat
            );
            const lastShippedDate = getFormattedDate(
              item?.lastShippedDate,
              talentDateFormat,
              true
            );
            const exceptionType = item?.priceExpTypeFlag
              ? "-"
              : getExceptionType(storeRetailValue, goiRetailValue);
            const itemTypes = getItemTypes(item);
            const tags = item.tags?.map((tag: string) => {
              if (ITEM_TAGS_MAPPER[tag]) {
                return ITEM_TAGS_MAPPER[tag];
              }
              return tag;
            });
            return {
              ...item,
              itemType,
              goiRetail,
              storeRetail,
              key: getComponentKey(),
              goiPriceChangeDate,
              storePriceChangeDate,
              lastSoldDate,
              exceptionType,
              lastShippedDate,
              itemTypes,
              tags
            };
          });
          state.recordsCount = payload?.data?.length || 0;
        } else {
          state.data = null;
          state.recordsCount = payload.recordsCount;
        }
      })
      .addCase(fetchPriceExceptionData.rejected, (state) => {
        state.status = "error";
        state.data = null;
      });
  }
});

export const selectViewItemAlertsTableData = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: PriceExceptionInitialState;
}) => viewItemAlertsTable.data;

export const selectViewItemAlertsTableApiStatus = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: PriceExceptionInitialState;
}) => viewItemAlertsTable.status;

export const selectViewItemAlertsTableError = ({
  viewItemAlertsTable
}: {
  viewItemAlertsTable: PriceExceptionInitialState;
}) => viewItemAlertsTable.error;

const getExceptionType = (storeRetailValue: number, goiRetailValue: number) => {
  let exceptionType = "-";
  if (goiRetailValue > storeRetailValue) {
    exceptionType = "markdown";
  }
  if (goiRetailValue < storeRetailValue) {
    exceptionType = "markup";
  }
  return exceptionType;
};

export default viewPriceExceptionData.reducer;
